import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, LayoutGrid, Layers } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const FloorRepair = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/floor-repair"]?.title} description={seoData["services/floor-repair"]?.description} path="/services/floor-repair" keywords={seoData["services/floor-repair"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Industrial Floor Coating & Repair"
        description="Industrial floor coating contractor in California. Heavy-duty epoxy, polyurethane, and resinous floor systems for manufacturing, warehouses, food processing, and industrial facilities. Slip-resistant industrial coatings and safety striping."
        backgroundKey="floor-repair"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Floor coating failure from heavy forklift traffic and impact',
              'Chemical spill damage and staining on concrete floors',
              'Slip hazards in wet or oily processing areas',
              'Concrete dusting and surface deterioration',
              'Joint failures and cracks in warehouse and production floors',
              'Non-compliance with OSHA safety marking requirements'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-amber-50 rounded-lg">
                <LayoutGrid className="text-amber-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Heavy-Duty Epoxy Floor Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Multi-coat epoxy floor systems engineered for heavy industrial use. Impact-resistant, chemical-resistant, and abrasion-resistant coatings for manufacturing, warehouses, maintenance shops, and loading docks. 20-125 mil systems.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Slip-Resistant & Safety Coatings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Textured, slip-resistant floor coatings for wet processing areas, ramps, and pedestrian walkways. OSHA-compliant safety striping, color-coded zones, and aisle marking for industrial facilities.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Polyurethane & MMA Floor Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Fast-cure MMA (methyl methacrylate) and polyurethane floor systems for rapid return-to-service. Weekend and overnight installations for facilities that cannot afford extended downtime.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Layers className="text-amber-600 mr-3 flex-shrink-0" size={22} />
                  Concrete Floor Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Joint repair, crack filling, spall repair, and concrete resurfacing to prepare deteriorated floors for coating. Complete floor rehabilitation from substrate repair through final topcoat.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Heavy-duty systems rated for forklift traffic, impact, and chemical exposure',
                  'Slip-resistant finishes for OSHA compliance in wet and oily areas',
                  'Fast-cure options for minimal production downtime',
                  'USDA/FDA compliant systems available for food processing',
                  'Complete floor rehabilitation — repair, prep, and coating in one contract',
                  'Safety striping and zone marking included',
                  '25+ years industrial floor coating experience',
                  'Manufacturer-backed warranties on all floor systems'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4111 (Magma-Quartz)</p>
                  <p className="text-xs text-slate-500 mt-1">Heavy-duty concrete floor repair</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4131 (Magma-Screed)</p>
                  <p className="text-xs text-slate-500 mt-1">Floor resurfacing and leveling</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5231 (SG-Laminate)</p>
                  <p className="text-xs text-slate-500 mt-1">Chemical-resistant floor coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4154 (Magma-CR4)</p>
                  <p className="text-xs text-slate-500 mt-1">Chemical-resistant floor repair</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Need Durable Industrial Floors?</h2>
          <p className="text-blue-100 mb-8">Contact our floor coating specialists for a free assessment and quote. We'll recommend the right system for your traffic, chemical, and performance requirements.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FloorRepair;
